import React, { useState, useEffect } from 'react'
import QRCode from 'qrcode'
import { registerPasskey, listPasskeys } from '../auth/webauthn.js'
import {
  associateTotpToken,
  verifyAndEnableTotp,
  disableTotp,
  isTotpEnabled,
  buildTotpUri,
  changePassword,
} from '../auth/totp.js'
import { Button, TextField } from '../ui'
import { ConfirmDialog } from '../ui/Modal/ConfirmDialog.jsx'

export function AccountSection({ user }) {
  return (
    <section style={styles.section}>
      <p style={styles.sectionLabel}>Account</p>
      <div style={styles.card}>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>Name</span>
          <span style={styles.fieldValue}>{user.name}</span>
        </div>
        <div style={{ ...styles.field, borderBottom: 'none', marginBottom: 0 }}>
          <span style={styles.fieldLabel}>Email</span>
          <span style={styles.fieldValue}>{user.email}</span>
        </div>
      </div>
    </section>
  )
}

export function SecuritySection({ user }) {
  const [passkeyCount, setPasskeyCount] = useState(null)
  const [passkeyLoading, setPasskeyLoading] = useState(false)
  const [passkeyError, setPasskeyError] = useState(null)
  const [passkeySuccess, setPasskeySuccess] = useState(false)

  const [totpEnabled, setTotpEnabled] = useState(null)
  const [totpSetupMode, setTotpSetupMode] = useState(false)
  const [totpSecret, setTotpSecret] = useState(null)
  const [totpQrDataUrl, setTotpQrDataUrl] = useState(null)
  const [totpCode, setTotpCode] = useState('')
  const [totpLoading, setTotpLoading] = useState(false)
  const [totpError, setTotpError] = useState(null)
  const [totpSuccess, setTotpSuccess] = useState(false)
  const [showDisableTotp, setShowDisableTotp] = useState(false)

  const [showChangePassword, setShowChangePassword] = useState(false)
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [passwordError, setPasswordError] = useState(null)
  const [passwordSuccess, setPasswordSuccess] = useState(false)

  useEffect(() => {
    listPasskeys(user.accessToken).then((data) => setPasskeyCount(data.count)).catch(() => {})
    isTotpEnabled(user.accessToken).then(setTotpEnabled).catch(() => setTotpEnabled(false))
  }, [])

  useEffect(() => {
    if (totpSecret && user.email) {
      QRCode.toDataURL(buildTotpUri(totpSecret, user.email), { width: 160, margin: 1 })
        .then(setTotpQrDataUrl)
        .catch(() => setTotpQrDataUrl(null))
    } else {
      setTotpQrDataUrl(null)
    }
  }, [totpSecret, user.email])

  async function handleAddPasskey() {
    setPasskeyError(null)
    setPasskeySuccess(false)
    setPasskeyLoading(true)
    try {
      await registerPasskey(user.accessToken)
      setPasskeySuccess(true)
      setPasskeyCount((c) => (c ?? 0) + 1)
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setPasskeyError('Registration cancelled.')
      } else {
        setPasskeyError(err.message || 'Registration failed')
      }
    } finally {
      setPasskeyLoading(false)
    }
  }

  async function handleStartTotpSetup() {
    setTotpError(null)
    setTotpSuccess(false)
    setTotpCode('')
    setTotpLoading(true)
    try {
      const secret = await associateTotpToken(user.accessToken)
      setTotpSecret(secret)
      setTotpSetupMode(true)
    } catch (err) {
      setTotpError(err.message || 'Failed to start TOTP setup')
    } finally {
      setTotpLoading(false)
    }
  }

  async function handleVerifyTotp() {
    setTotpError(null)
    setTotpLoading(true)
    try {
      await verifyAndEnableTotp(user.accessToken, totpCode.trim())
      setTotpEnabled(true)
      setTotpSetupMode(false)
      setTotpSecret(null)
      setTotpCode('')
      setTotpSuccess(true)
    } catch (err) {
      setTotpError(err.message || 'Verification failed')
    } finally {
      setTotpLoading(false)
    }
  }

  async function handleDisableTotp() {
    setTotpError(null)
    setTotpLoading(true)
    try {
      await disableTotp(user.accessToken)
      setTotpEnabled(false)
      setShowDisableTotp(false)
      setTotpSuccess(false)
    } catch (err) {
      setTotpError(err.message || 'Failed to disable TOTP')
    } finally {
      setTotpLoading(false)
    }
  }

  async function handleChangePassword() {
    setPasswordError(null)
    setPasswordSuccess(false)
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match')
      return
    }
    setPasswordLoading(true)
    try {
      await changePassword(user.accessToken, oldPassword, newPassword)
      setPasswordSuccess(true)
      setShowChangePassword(false)
      setOldPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err) {
      setPasswordError(err.message || 'Failed to change password')
    } finally {
      setPasswordLoading(false)
    }
  }

  return (
    <>
      <section style={styles.section}>
        <p style={styles.sectionLabel}>Security</p>
        <div style={styles.card}>
          <div style={styles.securityCheckRow}>
            <span style={styles.securityCheckIcon}>✓</span>
            <span style={styles.securityCheckLabel}>Secure password</span>
            <span style={styles.fieldValue}>Set</span>
          </div>
          <div style={styles.securityCheckRow}>
            <span style={passkeyCount > 0 ? styles.securityCheckIcon : styles.securityCheckIconWarn}>
              {passkeyCount > 0 ? '✓' : '✗'}
            </span>
            <span style={passkeyCount > 0 ? styles.securityCheckLabel : styles.securityCheckLabelWarn}>Passkey</span>
            <span style={passkeyCount > 0 ? styles.fieldValue : styles.securityValueWarn}>
              {passkeyCount === null ? '...' : passkeyCount > 0 ? `${passkeyCount} registered` : 'Not set up'}
            </span>
          </div>
          <div style={{ ...styles.securityCheckRow, borderBottom: 'none', marginBottom: 0 }}>
            <span style={totpEnabled ? styles.securityCheckIcon : styles.securityCheckIconWarn}>
              {totpEnabled ? '✓' : '✗'}
            </span>
            <span style={totpEnabled ? styles.securityCheckLabel : styles.securityCheckLabelWarn}>Authenticator (OTP)</span>
            <span style={totpEnabled ? styles.fieldValue : styles.securityValueWarn}>
              {totpEnabled === null ? '...' : totpEnabled ? 'Enabled' : 'Not set up'}
            </span>
          </div>

          {passkeySuccess && (
            <p style={{ fontSize: '12px', color: 'var(--success-text)', margin: '8px 0 0 0' }}>Passkey added successfully.</p>
          )}
          {passkeyError && (
            <p style={{ fontSize: '12px', color: 'var(--energy-1)', margin: '8px 0 0 0' }}>{passkeyError}</p>
          )}
          {totpSuccess && (
            <p style={{ fontSize: '12px', color: 'var(--success-text)', margin: '8px 0 0 0' }}>OTP enabled.</p>
          )}
          {totpError && (
            <p style={{ fontSize: '12px', color: 'var(--energy-1)', margin: '8px 0 0 0' }}>{totpError}</p>
          )}
          {passwordSuccess && (
            <p style={{ fontSize: '12px', color: 'var(--success-text)', margin: '8px 0 0 0' }}>Password changed successfully.</p>
          )}
          {passwordError && (
            <p style={{ fontSize: '12px', color: 'var(--energy-1)', margin: '8px 0 0 0' }}>{passwordError}</p>
          )}

          {showChangePassword && (
            <div style={styles.totpSetup}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <TextField
                  type="password"
                  placeholder="Current password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  aria-label="Current password"
                />
                <TextField
                  type="password"
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  aria-label="New password"
                />
                <TextField
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  aria-label="Confirm new password"
                />
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Button
                    size="sm"
                    onClick={handleChangePassword}
                    disabled={passwordLoading || !oldPassword || !newPassword || !confirmPassword}
                  >
                    {passwordLoading ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => { setShowChangePassword(false); setOldPassword(''); setNewPassword(''); setConfirmPassword(''); setPasswordError(null) }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}

          {totpSetupMode && totpSecret && (
            <div style={styles.totpSetup}>
              <p style={styles.totpInstructions}>
                Scan this code with your authenticator app, or copy the key manually.
              </p>
              <div style={styles.totpQrWrap}>
                {totpQrDataUrl && (
                  <img
                    src={totpQrDataUrl}
                    alt="TOTP QR code"
                    width="160"
                    height="160"
                    style={styles.totpQrImg}
                  />
                )}
              </div>
              <div style={styles.totpSecretRow}>
                <code style={styles.totpSecretCode}>{totpSecret}</code>
              </div>
              <div style={styles.totpVerifyRow}>
                <div style={{ flex: 1 }}>
                  <TextField
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="6-digit code"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    aria-label="6-digit verification code"
                  />
                </div>
                <Button
                  size="sm"
                  onClick={handleVerifyTotp}
                  disabled={totpLoading || totpCode.length !== 6}
                >
                  {totpLoading ? 'Verifying...' : 'Verify'}
                </Button>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => { setTotpSetupMode(false); setTotpSecret(null); setTotpCode(''); setTotpError(null) }}
              >
                Cancel
              </Button>
            </div>
          )}

          {!totpSetupMode && !showChangePassword && (
            <>
              <div style={styles.totpDivider} />
              <div style={styles.securityActions}>
                <Button variant="secondary" size="sm" onClick={() => { setShowChangePassword(true); setPasswordSuccess(false); setPasswordError(null) }}>
                  Change password
                </Button>
                <Button variant="secondary" size="sm" onClick={handleAddPasskey} disabled={passkeyLoading}>
                  {passkeyLoading ? 'Registering...' : 'Add a passkey'}
                </Button>
                {totpEnabled ? (
                  <Button variant="danger" size="sm" onClick={() => setShowDisableTotp(true)} disabled={totpLoading}>
                    Disable OTP
                  </Button>
                ) : (
                  <Button variant="secondary" size="sm" onClick={handleStartTotpSetup} disabled={totpLoading}>
                    {totpLoading ? 'Setting up...' : 'Set up OTP'}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      <ConfirmDialog
        open={showDisableTotp}
        title="Disable OTP?"
        message="Your account will no longer require a verification code when signing in with a password."
        confirmLabel={totpLoading ? 'Disabling...' : 'Disable'}
        onConfirm={handleDisableTotp}
        onCancel={() => setShowDisableTotp(false)}
        tone="danger"
      />
    </>
  )
}

const styles = {
  section: { marginBottom: '24px' },
  sectionLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '8px',
  },
  card: {
    background: 'var(--surface, var(--sidebar-bg))',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    padding: '16px',
  },
  field: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
    borderBottom: '1px solid var(--border)',
    marginBottom: '4px',
  },
  fieldLabel: { fontSize: '12px', color: 'var(--text-secondary)', flex: 1, minWidth: 0, wordBreak: 'break-word' },
  fieldValue: { fontSize: '13px', color: 'var(--text-primary)', flexShrink: 0, textAlign: 'right' },
  securityCheckRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 0',
    borderBottom: '1px solid var(--border)',
    marginBottom: '4px',
  },
  securityCheckIcon: {
    fontSize: '13px', color: 'var(--success-text)', fontWeight: 700, width: '16px', textAlign: 'center', flexShrink: 0,
  },
  securityCheckIconWarn: {
    fontSize: '13px', color: 'var(--info-text)', fontWeight: 700, width: '16px', textAlign: 'center', flexShrink: 0,
  },
  securityCheckLabel: {
    fontSize: '12px', color: 'var(--text-secondary)', flex: 1, minWidth: 0, wordBreak: 'break-word',
  },
  securityCheckLabelWarn: {
    fontSize: '12px', color: 'var(--info-text)', flex: 1, minWidth: 0, wordBreak: 'break-word',
  },
  securityValueWarn: {
    fontSize: '13px', color: 'var(--info-text)', flexShrink: 0, textAlign: 'right',
  },
  securityActions: {
    display: 'flex', flexWrap: 'wrap', gap: '8px',
  },
  secondaryBtn: {
    padding: '6px 12px',
    fontSize: '12px',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    background: 'none',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
  },
  dangerBtn: {
    padding: '6px 12px',
    fontSize: '12px',
    border: '1px solid var(--danger-text)',
    borderRadius: '4px',
    background: 'none',
    color: 'var(--danger-text)',
    cursor: 'pointer',
  },
  saveBtn: {
    padding: '4px 10px',
    fontSize: '11px',
    border: 'none',
    borderRadius: '4px',
    background: 'var(--text-primary)',
    color: '#fff',
    fontWeight: 600,
    cursor: 'pointer',
  },
  totpDivider: { borderTop: '1px solid var(--border)', margin: '12px 0' },
  totpSetup: { marginTop: '12px' },
  totpInstructions: { fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 10px 0' },
  totpQrWrap: { display: 'flex', justifyContent: 'center', margin: '0 0 10px 0' },
  totpQrImg: { borderRadius: '6px', border: '1px solid var(--border)' },
  totpSecretRow: { display: 'flex', justifyContent: 'center', marginBottom: '12px' },
  totpSecretCode: {
    fontSize: '12px',
    fontFamily: 'monospace',
    letterSpacing: '0.05em',
    color: 'var(--text-secondary)',
    background: 'var(--bg)',
    padding: '4px 8px',
    borderRadius: '4px',
    border: '1px solid var(--border)',
    userSelect: 'all',
    wordBreak: 'break-all',
  },
  totpVerifyRow: { display: 'flex', gap: '8px', alignItems: 'center' },
  totpCancelBtn: {
    marginTop: '8px',
    padding: '0',
    fontSize: '11px',
    border: 'none',
    background: 'none',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
}
